/**
 * voice-v8 — Live-Diktat (#302 Streaming-STT, 15.07.).
 *
 * Nimmt das Mikrofon auf, streamt rohes PCM DIREKT an Soniox' Echtzeit-
 * WebSocket (Kurzzeit-Schlüssel vom Server) und liefert live wachsenden Text:
 *  - final:  bestätigt, ändert sich nie mehr.
 *  - interim: vorläufig, kann sich noch ändern (sofortiges Feedback).
 *
 * ISOLIERT: eigener getUserMedia-Stream + eigene WebSocket. Fasst den PTT-
 * Kommando-Pfad und die Player NICHT an. Der echte Soniox-Key bleibt am Server.
 *
 * ⚠️ Braucht Soniox-Guthaben (sonst 402) und ist auf dem Gerät zu testen.
 */

import {
  acquireDictationStream,
  hoereAufLeitungsWechsel,
  jetzigeLeitung,
} from "./ptt.js";
import { normWort } from "../../shared/sende-wort.js";
import type { LiveWort } from "./jetzt-senden.js";
import { istEndeToken, type EndeSignal } from "./ende-token.js";
import { mitJevSchreibweise } from "./jev-konto.js";
import { meldeMikroAnlass, meldeSpur } from "./mikro-spur.js";
import { Heruntertakter } from "./pcm-takt.js";
import type { LiveStand } from "../../shared/live-stand.js";
import { istDiktatFloskel } from "../../shared/diktat-floskeln.js";
import { SONIOX_MODELL_ECHTZEIT } from "../../shared/soniox-modelle.js";
import { taktZusatz, wacheRechentakt, type TaktWache } from "./rechentakt.js";

const SONIOX_WS = "wss://stt-rt.soniox.com/transcribe-websocket";

interface DictationCallbacks {
  /**
   * final = bestätigter Text, interim = vorläufiger Rest. `woerter`: die
   * bestätigten Wörter mit ihren Zeitmarken (26.09.2026 spät, für die
   * Sprechpause vor einem Endwort — jetzt-senden.ts `pauseAusZeiten`).
   *
   * `ganz`: bestätigt + vorläufig als EIN Text (03.10.2026, pre.3625). Die
   * Grenze dazwischen kennt nur Soniox: „ wie“ beginnt ein neues Wort, „den“
   * setzt „Sen“ fort. `final`/`interim` sind getrimmt — wer sie selbst
   * zusammenfügt, klebt („Weltwie“) oder trennt („Sen den“) falsch.
   */
  onText: (
    final: string,
    interim: string,
    woerter: readonly LiveWort[],
    alle: readonly LiveWort[],
    ganz: string,
  ) => void;
  onError: (message: string) => void;
  /** Leitung steht, Audio fliesst — ab JETZT wird alles aufgenommen. */
  onReady?: () => void;
  /** Soniox meldete Sitzungsende (finished). */
  onFinished?: () => void;
  /**
   * Die Leitung ist zugegangen, OHNE dass jemand `stop()` gerufen hat, OHNE `finished` und OHNE vorherigen Fehler (`onError`): sie ist
   * still gestorben. Ohne diesen Rückruf bliebe die Seite ewig auf „hört“. Optional: wer ihn nicht übergibt (die V8-Seiten), merkt keine Änderung.
   */
  onGeschlossen?: (grund: string) => void;
  /**
   * 2026-07-25: Lautstaerke-Pegel (0..1) aus der laufenden Verarbeitung. Wir
   * haben die Audio-Daten hier ohnehin in der Hand — ein zweiter AudioContext
   * nur zum Anzeigen brachte in WKWebView das Diktat zum Schweigen.
   */
  onLevel?: (level: number) => void;
  /**
   * 01.10.2026 (Ticket 2867): Soniox hat ein Satzende gemeldet (`<end>`). NUR
   * zum Messen (ende-token.ts) — das Signal ändert weder Text noch Entscheidung.
   * Gerufen nach `onText` derselben Nachricht, damit dessen Wörter schon da sind.
   */
  onEnde?: (e: EndeSignal) => void;
  /**
   * 28.10.2026 (Testballon): `max_endpoint_delay_ms` (500…3000) — wie spät Soniox das Satzende SPÄTESTENS meldet.
   * Fehlt es (oder 0), geht das Feld nicht mit und die Anfrage bleibt Byte für Byte wie sie war (Hauptweg).
   */
  endpointVerzoegerungMs?: number;
  /**
   * 28.10.2026 (Testballon, Latenz): Größe des Mikrofonblocks in Samples (1024, 2048 oder 4096). Kleiner = der Ton erreicht Soniox
   * früher (4096 bei 48 kHz = 85 ms Verzug je Block). Fehlt es, bleibt es bei 4096 wie im Hauptweg.
   */
  blockGroesse?: number;
  /**
   * 28.10.2026 (Testballon): `language_hints` für Soniox, z. B. ["de","en","es"] (Mischsprache erlaubt). Fehlt es oder ist es leer,
   * geht wie bisher genau ["de"] mit — der Hauptweg bleibt Byte für Byte gleich.
   */
  sprachHinweise?: readonly string[];
}

interface SonioxToken {
  text?: string;
  is_final?: boolean;
  /** Beginn des Wortes auf der Tonachse (26.09.2026 spät: für die Sprechpause). */
  start_ms?: number;
  /** Ende des Wortes auf der Tonachse, ab dem ersten gesendeten Rahmen. */
  end_ms?: number;
  /** 03.10.2026 (Fantasiewörter): wie sicher die Erkennung ist (0…1) — nur gemessen. */
  confidence?: number;
}

/**
 * Ein bestätigtes Stück an die Wörter hängen. (26.09.2026 spät)
 *
 * Soniox liefert Teilwörter; ein neues Wort beginnt mit Leerraum („ Sen",
 * „den"). Reine Satzzeichen („,") gehören zum Wort davor — sonst zählten
 * sie als Wort und verschöben die Zuordnung zum Text.
 */
export function haengeWortAn(
  woerter: LiveWort[],
  t: { text?: string; start_ms?: number; end_ms?: number; confidence?: number },
): void {
  const text = t.text ?? "";
  if (!text) return;
  const start = typeof t.start_ms === "number" ? t.start_ms : null;
  const ende = typeof t.end_ms === "number" ? t.end_ms : null;
  // 03.10.2026: die Konfidenz nur, wenn die Erkennung sie nennt — je Wort die kleinste.
  const konf = typeof t.confidence === "number" && Number.isFinite(t.confidence) ? t.confidence : null;
  const letztes = woerter.at(-1);
  if (!letztes || (/^\s/.test(text) && normWort(text) !== "")) {
    woerter.push({ text: text.trim(), startMs: start, endMs: ende, ...(konf !== null ? { konfidenz: konf } : {}) });
    return;
  }
  letztes.text += text;
  if (ende !== null) letztes.endMs = ende;
  if (konf !== null) letztes.konfidenz = Math.min(letztes.konfidenz ?? konf, konf);
}
interface SonioxMessage {
  tokens?: SonioxToken[];
  finished?: boolean;
  error_code?: number | string;
  error_message?: string;
}

export class LiveDictation {
  /** Wurde der Strom von einer laufenden Aufnahme geliehen? */
  private geliehen = false;
  /**
   * Ticket 2518 (24.09.2026): Abmeldung vom Leitungswechsel der Sprechtaste.
   * Nur bei geliehenem Strom — ein eigener wird nie getauscht.
   */
  private leitungAbmelden: (() => void) | null = null;

  /*
   * ⚠️ 2026-08-28, Martin: „Wie wird das Wetter morgen in Hollenthon" kam
   * als „In Hollenthon" an — die erste Sekunde fehlte.
   *
   *   Ursache: `onaudioprocess` warf JEDEN Rahmen weg, solange die
   *   WebSocket noch nicht offen war. Zwischen Mikrofon-Start und offener
   *   Leitung liegen Schluessel-Abruf und Verbindungsaufbau — gemessen
   *   rund 800 ms. Genau so lange war er schon am Sprechen.
   *
   *   Solange die Erkennung nur ANGEZEIGT wurde, fiel das kaum auf. Seit
   *   der Satz auch abgeschickt wird, fehlt der Anfang der Frage.
   *
   *   Jetzt wird gepuffert und beim Verbindungsaufbau nachgereicht. Der
   *   Deckel ist grosszuegig, aber endlich: kommt die Leitung nie
   *   zustande, darf der Puffer nicht wachsen, bis der Speicher voll ist.
   */
  private vorlauf: ArrayBuffer[] = [];
  /** Rund drei Sekunden bei 4096er Rahmen (bei kleineren Blöcken entsprechend mehr Blöcke, siehe `vorlaufMax`). */
  private static readonly VORLAUF_MAX = 36;
  /** Der Mikrofonblock: nur 1024/2048/4096 sind erlaubt, alles andere bleibt bei 4096. */
  private blockGroesse(): number {
    const b = this.cb.blockGroesse;
    return b === 1024 || b === 2048 ? b : 4096;
  }
  private vorlaufMax(): number {
    return Math.ceil((LiveDictation.VORLAUF_MAX * 4096) / this.blockGroesse());
  }
  private ws: WebSocket | null = null;
  private sttContext = "";
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  /*
   * Ticket 2634 (26.09.2026): die Wache über den Rechentakt (rechentakt.ts).
   * Bei 61 von 68 Drücken mit Pegel 0/0/0 schickte dieses Diktat 0 KB — sein
   * `onaudioprocess` lief nie, der Kontext rechnete nicht. Was eine Rettung
   * zurücklässt, räumt stop() ab: den alten Kontext erst dann (seine Quelle
   * liest die Spur der Aufnahme), die Kopie der Spur ebenfalls.
   */
  private taktWache: TaktWache | null = null;
  private takt: Heruntertakter | null = null;
  private aufTon: ((e: AudioProcessingEvent) => void) | null = null;
  private abgeloesteKontexte: AudioContext[] = [];
  private spurKopien: MediaStreamTrack[] = [];
  private finalText = "";
  /** 26.09.2026 spät: die bestätigten Wörter mit Zeitmarken (haengeWortAn). */
  private finaleWoerter: LiveWort[] = [];
  /** Ende des letzten bestätigten Wortes auf der Tonachse (ms) — für den Abstand eines `<end>`. */
  private letztesWortEndeMs = -1;
  /** 2026-07-25: Zeitpunkt des ersten gesendeten Audio-Bildes (Messung). */
  private ersteBildMs = 0;
  /** Erstes eingetroffenes Wort — misst die echte Verzoegerung der Kette. */
  private ersteAntwortGemeldet = false;
  /*
   * Ticket 2519 (23.09.2026) — EINE Zeile `[diktat]` je Aufnahme.
   *
   * Martin, 23.09., 09:16, unterwegs: die Mitschrift „rückt sehr langsam
   * nach". Im Journal stand dazu NICHTS: die einzige Messung („erstes Wort
   * nach …ms") ging nur in die Konsole des Geräts, und die sieht hier
   * niemand. Ob der Sendepuffer wächst (Netz zu langsam für rohes PCM in
   * Kontext-Rate, bei 48 kHz rund 96 KB/s) oder die Erkennung hinterherhängt,
   * war darum nicht zu unterscheiden.
   *
   * ⚠️ Nur messen. Ob vor dem Senden auf 16 kHz heruntergetaktet wird,
   *    entscheidet erst `pufferMax` aus echten Aufnahmen unterwegs.
   */
  private messung = {
    /** Wanduhr beim ANFANG des ersten Rahmens — Nullpunkt der Soniox-Tonachse. */
    tonNullMs: null as number | null,
    rate: 0,
    gesendetBytes: 0,
    /** Größter gesehener `ws.bufferedAmount` — wächst er, trägt das Netz nicht. */
    pufferMax: 0,
    /**
     * Tondauer der Rahmen, die vor dem Verbindungsaufbau über den Deckel
     * fielen — in ms, weil genau um so viel die Soniox-Tonachse hinter der
     * Wanduhr zurückbleibt.
     */
    vorlaufVerlorenMs: 0,
    /** Wo auf der Soniox-Tonachse die Lücke liegt: am Ende des Vorlaufs. */
    lueckeAbMs: null as number | null,
    erstesWortMs: -1,
    /** Je Nachricht mit finalen Wörtern: Wanduhr seit Tonanfang minus end_ms. */
    nachlaeufe: [] as number[],
    gemeldet: false,
  };
  /** Wird aufgeloest, sobald Soniox den Abschluss meldet (siehe warteAufAbschluss). */
  private abschlussFertig: (() => void) | null = null;
  private stopped = false;
  /*
   * 12.10.2026 (Bahn live-primaer): was der Server wissen muss, um dem Live-Text zu trauen (stand()).
   * Nur gemerkt, nichts davon ändert die Mitschrift selbst.
   */
  /** Soniox hat `finished` gemeldet — erst dann steht fest, dass kein Wort mehr nachkommt. */
  private abschlussGesehen = false;
  /** Stumm (Mute der Seiten /neun und Playground): nur Nullen gehen weiter, die Leitung bleibt offen. Gilt auch auf Spurkopien und nach jeder Rettung. */
  private stumm = false;
  private nullen = new Float32Array(0);
  /** Die Verbindung riss (Fehler, Fehlercode, Schluss) bevor stop() gerufen wurde: ein Stück fehlt vielleicht. */
  private abgebrochen = false;
  /** `onError` wurde schon gerufen: ein folgendes `close` meldet nicht ein zweites Mal. */
  private fehlerGemeldet = false;
  /** Wie lange stopUndAbschluss auf den Abschluss wartete (ms); `null`: nie gewartet. */
  private abschlussMs: number | null = null;

  constructor(private cb: DictationCallbacks) {}

  /**
   * Stumm schalten: die Spur (und jede Kopie aus der Rettung) wird gesperrt UND `aufTon` schickt Nullen statt Ton, damit
   * nichts durchkommt, auch wenn eine Kopie ihr eigenes `enabled` hat. Die Verbindung bleibt, Soniox bekommt Stille.
   */
  setzeStumm(an: boolean): void {
    this.stumm = an;
    for (const t of this.stream?.getAudioTracks() ?? []) t.enabled = !an;
    for (const t of this.spurKopien) t.enabled = !an;
  }

  /** Der laufende Mikro-Stream — fuer einen externen Pegel-Visualizer. */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Läuft der Ton-Kontext (nach `resume`, höchstens `fristMs`)? iOS lässt einen Kontext ohne Fingertipp „suspended“; die Wache des
   * Gesprächs (lib/neun-halbduplex.ts) fällt dann auf das offene Mikrofon zurück, statt ein totes Mikrofon zu melden.
   */
  async kontextBereit(fristMs: number): Promise<boolean> {
    const c = this.ctx;
    if (!c) return false;
    if (c.state !== "running") {
      let t: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([c.resume().catch(() => undefined), new Promise<void>((r) => void (t = setTimeout(r, fristMs)))]);
      clearTimeout(t);
    }
    return this.ctx === c && c.state === "running";
  }

  /**
   * Diktat starten. Wirft bei fehlendem Mikro/Schluessel.
   *
   * @param geliehenerStrom 2026-08-28: Laeuft schon eine Aufnahme (PTT), wird
   *   DEREN Strom mitgelesen, statt ein zweites Mikrofon zu oeffnen. Zwei
   *   parallele getUserMedia sind auf iOS die Abriss-Ursache. Ein geliehener
   *   Strom wird beim Stoppen NICHT beendet — er gehoert dem Aufnehmer.
   */
  async start(geliehenerStrom?: MediaStream | null): Promise<void> {
    this.stopped = false;
    this.finalText = "";
    this.finaleWoerter = [];
    this.letztesWortEndeMs = -1;
    this.abschlussGesehen = false;
    this.abgebrochen = false;
    this.fehlerGemeldet = false;
    this.abschlussMs = null;
    this.geliehen = Boolean(geliehenerStrom);

    // iOS: AudioContext MUSS in der Finger-Geste entstehen, VOR jedem await.
    // Nach getUserMedia ist die Geste verbraucht — der Kontext bleibt
    // "suspended", ScriptProcessor feuert nie, Soniox bekommt Stille,
    // im Feld erscheint kein Text (Martin, Tagesfrage 01.09.).
    const AC =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) throw new Error("AudioContext fehlt");
    this.ctx = new AC();
    void this.ctx.resume();

    // 1) Kurzzeit-Schlüssel und Mikrofon PARALLEL holen — das Mikro kommt
    //    ueber die EINE PTT-Leitung (warm uebernehmen oder Single-Flight).
    const keyPromise = (async (): Promise<string> => {
      const keyRes = await fetch("/api/stt/realtime-key", {
        method: "POST",
        credentials: "same-origin",
      });
      const keyJson = (await keyRes.json().catch(() => ({}))) as {
        ok?: boolean;
        apiKey?: string;
        error?: string;
        context?: string;
      };
      if (!keyRes.ok || !keyJson.ok || !keyJson.apiKey) {
        throw new Error(
          keyJson.error || `Schlüssel-Fehler (HTTP ${keyRes.status})`,
        );
      }
      // 2026-07-24 (Martin): Eigennamen-Kontext fuers Soniox-Biasing.
      this.sttContext = keyJson.context ?? "";
      return keyJson.apiKey;
    })();
    const micPromise = geliehenerStrom
      ? Promise.resolve(geliehenerStrom)
      : acquireDictationStream();
    // Review 23.07. (HOCH): scheitert der Key-Fetch, ist der warme
    // PTT-Stream bereits synchron uebernommen (takeWarmStream) — ohne
    // Aufraeumen bliebe das Mikro offen (iOS: Audio-Session haengt in
    // playAndRecord, Wiedergabe leise am Ohrhoerer). Im Fehlerpfad den
    // schon erworbenen Stream sicher stoppen.
    let tempKey: string;
    let micStream: MediaStream;
    try {
      /*
       * ⚠️ 2026-08-28: Hier stand ein `Promise.all` — der Ton-Graph entstand
       * erst, wenn AUCH der Schluessel da war. Die Zeit dazwischen sprach
       * Martin bereits, und sie war verloren.
       *
       * Jetzt: Mikro zuerst (bei geliehenem Strom sofort da), Graph bauen,
       * puffern — und ERST DANN auf den Schluessel warten. Der Vorlauf
       * geht beim Verbindungsaufbau nach.
       */
      micStream = await micPromise;
    } catch (err) {
      void this.ctx?.close().catch(() => undefined);
      this.ctx = null;
      void micPromise
        // ⚠️ NUR einen selbst geholten Strom beenden — ein geliehener gehoert
        // der laufenden Aufnahme und waere hier mitten im Satz tot.
        .then((st) => {
          if (!this.geliehen) st?.getTracks().forEach((t) => t.stop());
        })
        .catch(() => undefined);
      throw err;
    }
    /*
     * Ticket 2518: Tauscht die Sprechtaste ihre tote Leitung, liest die
     * Mitschrift auf der neuen weiter. `jetzigeLeitung` fängt den Wechsel,
     * der schon VOR diesem Start geschah; `folgeDerLeitung` jeden späteren.
     */
    this.stream = this.geliehen ? jetzigeLeitung(micStream) : micStream;
    if (this.ctx.state !== "running") await this.ctx.resume();
    const sampleRate = Math.round(this.ctx.sampleRate);
    // Ticket 2519 (24.09.2026): gesendet wird mit 16 kHz — Begründung und
    // Messung in pcm-takt.ts. Die Pegelanzeige rechnet weiter am Original.
    const takt = new Heruntertakter(sampleRate);
    this.takt = takt;
    this.messung.rate = takt.ziel;
    this.source = this.ctx.createMediaStreamSource(this.stream);
    // ScriptProcessor (deprecated, aber in WKWebView zuverlässig verfügbar —
    // AudioWorklet bräuchte eine separate Modul-Datei + Ladepfad).
    this.processor = this.ctx.createScriptProcessor(this.blockGroesse(), 1, 1);

    // 4) WebSocket zu Soniox öffnen.
    const aufTon = (e: AudioProcessingEvent): void => {
      if (!this.ersteBildMs) this.ersteBildMs = performance.now();
      // Ticket 2519: die Tonachse beginnt am ANFANG dieses Rahmens.
      this.messung.tonNullMs ??= this.ersteBildMs - e.inputBuffer.duration * 1000;
      let input = e.inputBuffer.getChannelData(0);
      if (this.stumm) {
        if (this.nullen.length !== input.length) this.nullen = new Float32Array(input.length);
        input = this.nullen;
      }
      // Ticket 2634: nach einer Rettung gilt der Takter des neuen Kontexts.
      const pcm = (this.takt ?? takt).verarbeite(input);
      // 2026-07-25: Effektivwert am Originalton — kostet praktisch nichts
      // und erspart einen zweiten AudioContext.
      let summe = 0;
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i] ?? 0));
        summe += s * s;
      }
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(pcm.buffer);
        this.merkeGesendet(this.ws, pcm.buffer.byteLength);
      } else if (this.vorlauf.length < this.vorlaufMax()) {
        // Leitung steht noch nicht — aufheben, nicht wegwerfen.
        this.vorlauf.push(pcm.buffer);
      } else {
        // Ticket 2519: bisher still verloren — jetzt wenigstens gezählt.
        this.messung.lueckeAbMs ??= this.vorlauf.length * e.inputBuffer.duration * 1000;
        this.messung.vorlaufVerlorenMs += e.inputBuffer.duration * 1000;
      }
      if (this.cb.onLevel) {
        try {
          // Gleiche Kennlinie wie im Visualizer (2026-07-25): Wurzel statt
          // reiner Multiplikation, damit leise Sprache sichtbar ausschlaegt.
          this.cb.onLevel(
            Math.min(1, Math.pow(Math.sqrt(summe / input.length) * 14, 0.6)),
          );
        } catch {
          /* Anzeige ist Kuer */
        }
      }
    };
    this.aufTon = aufTon;
    this.processor.onaudioprocess = aufTon;
    this.source.connect(this.processor);
    // ScriptProcessor braucht eine Ziel-Verbindung, um zu feuern; ein
    // stummer Gain verhindert Rückkopplung/hörbare Ausgabe.
    const mute = this.ctx.createGain();
    mute.gain.value = 0;
    this.processor.connect(mute);
    mute.connect(this.ctx.destination);
    if (!this.stopped) {
      this.taktWache = wacheRechentakt(this.ctx, {
        neuBauen: () => this.baueNeu(),
        melde: (e) => meldeMikroAnlass(`rechentakt-${e.stufe}`, taktZusatz("diktat", e)),
      });
    }
    if (this.geliehen) this.folgeDerLeitung();
      try {
        tempKey = await keyPromise;
      } catch (err) {
        this.leitungAbmelden?.();
        this.leitungAbmelden = null;
        this.stream?.getTracks().forEach((t) => {
          if (!this.geliehen) t.stop();
        });
        throw err;
      }
    const ws = new WebSocket(SONIOX_WS);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.addEventListener("open", () => {
      ws.send(
        JSON.stringify({
          api_key: tempKey,
          model: SONIOX_MODELL_ECHTZEIT,
          // ⚠️ 24.09.2026 (Ticket 2519): Hier stand „rohes PCM in der ECHTEN
          //    Kontext-Rate — kein Downsampling". 48 kHz stauten sich unterwegs
          //    bis 16 s im Sendepuffer (pcm-takt.ts). Die Rate kommt aus dem
          //    Heruntertakter, damit Angabe und Ton nie auseinanderlaufen.
          audio_format: "pcm_s16le",
          sample_rate: takt.ziel,
          num_channels: 1,
          language_hints: this.cb.sprachHinweise?.length ? [...this.cb.sprachHinweise] : ["de"],
          enable_endpoint_detection: true,
          ...(this.cb.endpointVerzoegerungMs ? { max_endpoint_delay_ms: this.cb.endpointVerzoegerungMs } : {}),
          // 2026-07-24 (Martin): Eigennamen-Kontext (Fohlenhof, Deike …)
          // biast die Erkennung — nur senden, wenn vorhanden.
          ...(this.sttContext ? { context: this.sttContext } : {}),
        }),
      );
      // 2026-08-28: Erst den Vorlauf nachreichen, DANN erst gilt die Leitung
      // als bereit — sonst fehlt der Anfang des Satzes.
      for (const rahmen of this.vorlauf) ws.send(rahmen);
      this.merkeGesendet(
        ws,
        this.vorlauf.reduce((n, r) => n + r.byteLength, 0),
      );
      this.vorlauf = [];
      // Leitung steht — ab jetzt geht jedes Wort in die Erkennung.
      this.cb.onReady?.();
    });

    ws.addEventListener("message", (ev) => {
      let msg: SonioxMessage;
      try {
        msg = JSON.parse(String(ev.data)) as SonioxMessage;
      } catch {
        return;
      }
      if (msg.error_code) {
        this.abgebrochen = true;
        this.fehlerGemeldet = true;
        this.cb.onError(`Soniox ${msg.error_code}: ${msg.error_message ?? ""}`);
        void this.stop();
        return;
      }
      let interim = "";
      let letztesEnde = -1;
      const enden: EndeSignal[] = [];
      // 03.10.2026 (Fantasiewörter): die vorläufigen Stücke mit Zeitmarken, für `alle` unten.
      const vorlaeufig: SonioxToken[] = [];
      for (const t of msg.tokens ?? []) {
        if (!t.text) continue;
        // Steuer-Tokens der Endpoint-Erkennung ("<end>" & Co.) sind KEIN
        // Text — Martin sah sie als "kleines Zeichen am Satzende".
        if (/^<[a-z_]+>$/i.test(t.text.trim())) {
          // Ticket 2867: das Satzende-Signal wird gemessen (ende-token.ts),
          // nicht benutzt. Es zählt nicht als Wort und nicht als Wortende.
          if (istEndeToken(t.text)) {
            enden.push({
              abstandMs: this.nachlaufMs(this.letztesWortEndeMs),
              wortzahl: this.finaleWoerter.length,
            });
          }
          continue;
        }
        if (t.is_final) {
          this.finalText += t.text;
          haengeWortAn(this.finaleWoerter, t);
          if (typeof t.end_ms === "number") {
            letztesEnde = Math.max(letztesEnde, t.end_ms);
            this.letztesWortEndeMs = Math.max(this.letztesWortEndeMs, t.end_ms);
          }
        } else {
          interim += t.text;
          vorlaeufig.push(t);
        }
      }
      // Ticket 2519: wie weit das eben bestätigte Wort hinter der Uhr liegt.
      const m = this.messung;
      if (letztesEnde >= 0) {
        // Die Lücken-Rechnung steht in nachlaufMs.
        const nachlauf = this.nachlaufMs(letztesEnde);
        if (nachlauf !== null) m.nachlaeufe.push(nachlauf);
      }
      // 2026-07-25 (Martin: "gibt es schnellere Sachen als Soniox?"):
      // einmal pro Diktat messen, wie lange das erste Wort braucht. Ohne
      // diese Zahl waere jede Aussage ueber "zu langsam" geraten.
      if (!this.ersteAntwortGemeldet && (this.finalText || interim)) {
        this.ersteAntwortGemeldet = true;
        if (this.ersteBildMs) {
          this.messung.erstesWortMs = Math.round(
            performance.now() - this.ersteBildMs,
          );
          console.log(`[diktat] erstes Wort nach ${this.messung.erstesWortMs}ms`);
        }
      }
      // Halluzination bei Stille: Steht am Ende NUR eine bekannte Floskel
      // (oder ein Ortsname aus dem Wortschatz), war da nichts Gesprochenes.
      const fertig = this.finalText.trim();
      if (fertig && !interim.trim() && istDiktatFloskel(fertig)) {
        console.log(`[diktat] Halluzination verworfen: "${fertig}"`);
        this.finalText = "";
        this.finaleWoerter = [];
        this.letztesWortEndeMs = -1;
        this.cb.onText("", "", [], [], "");
        return;
      }
      this.cb.onText(
        mitJevSchreibweise(fertig),
        mitJevSchreibweise(interim.trim()),
        this.finaleWoerter.map((w) => ({ ...w })),
        this.alleWoerter(vorlaeufig),
        // pre.3625: vor dem Trimmen verbinden — das Leerzeichen trägt Soniox' Wortgrenze.
        mitJevSchreibweise((this.finalText + interim).trim()),
      );
      for (const e of enden) {
        try {
          this.cb.onEnde?.(e);
        } catch {
          /* Messen ist Kür */
        }
      }
      if (msg.finished) {
        this.abschlussGesehen = true;
        // 2026-07-25: Wer auf den Abschluss wartet (stopUndAbschluss), wird
        // hier geweckt — DANN steht der vollstaendige Text fest.
        this.abschlussFertig?.();
        this.abschlussFertig = null;
        this.cb.onFinished?.();
        void this.stop();
      }
    });

    ws.addEventListener("error", () => {
      if (!this.stopped) {
        this.abgebrochen = true;
        this.fehlerGemeldet = true;
        this.cb.onError("WebSocket-Fehler");
      }
    });
    // Ticket 2519: die Leitung geht immer zu — nach „finished", nach der
    // Frist in stop() oder im Fehlerfall. Erst dann sind alle Wörter da.
    ws.addEventListener("close", () => {
      // Zugegangen, ohne dass jemand stop() gerufen hat: die Leitung ist mitten in der Aufnahme gerissen.
      const still = !this.stopped && !this.abschlussGesehen && !this.fehlerGemeldet;
      if (!this.stopped) this.abgebrochen = true;
      this.meldeMessung();
      if (still) {
        try {
          this.cb.onGeschlossen?.("leitung-zu");
        } catch {
          /* die Seite darf die Leitung nicht stören */
        }
      }
    });

    // 5) Audio-Frames als Int16LE an die WebSocket schieben.
  }

  /**
   * Ab jetzt jedem Leitungswechsel der Sprechtaste folgen. (Ticket 2518)
   *
   * ⚠️ Nachprüfung 24.09.2026: Erst NACH dem Aufbau des Ton-Graphen
   *    anmelden. Vorher stand die Anmeldung vor `ctx.resume()` — warf ein
   *    Schritt danach, blieb der Hörer für immer im Set der Sprechtaste
   *    (app.ts verwirft die Mitschrift dann ohne stop()). Ab hier räumt
   *    jeder Ausgang ab: stop() und der Fehlerzweig des Schlüssels.
   *    Ein Wechsel WÄHREND des Aufbaus wird darum hier nachgeholt. Kam
   *    stop() schon während des Aufbaus, gar nicht erst anmelden — abmelden
   *    würde niemand mehr.
   */
  private folgeDerLeitung(): void {
    if (this.stopped) return;
    this.leitungAbmelden?.();
    this.leitungAbmelden = hoereAufLeitungsWechsel((alt, neu) => {
      if (alt === this.stream) this.leseWeiterAuf(neu);
    });
    const jetzt = this.stream ? jetzigeLeitung(this.stream) : null;
    if (jetzt && jetzt !== this.stream) this.leseWeiterAuf(jetzt);
  }

  /**
   * Die Sprechtaste hat ihre tote Leitung mitten im Druck gegen eine frische
   * getauscht (Ticket 2518): auf der neuen weiterlesen. Verbindung, Kontext
   * und der Text bis hierher bleiben — auf der toten Leitung war keiner.
   *
   * ⚠️ Die alte Spur NICHT stoppen: sie gehört der Aufnahme, und die hat sie
   *    schon beendet. Hier wird nur die Quelle im Ton-Graphen umgesteckt.
   */
  private leseWeiterAuf(neu: MediaStream): void {
    if (this.stopped) return;
    this.stream = neu;
    // Steht der Graph noch nicht, baut start() ihn gleich aus this.stream.
    if (!this.ctx || !this.source || !this.processor) return;
    try {
      this.source.disconnect();
    } catch {
      /* schon getrennt */
    }
    this.source = this.ctx.createMediaStreamSource(neu);
    this.source.connect(this.processor);
  }

  /**
   * Ticket 2634, Stufe 2 der Wache: ein frischer Kontext auf einer KOPIE der
   * Spur, derselbe Verarbeiter, dieselbe Senderate (Soniox kennt nur die
   * eine aus dem Anfang der Verbindung). Der alte Kontext bleibt offen bis
   * stop() — seine Quelle liest die Spur der Aufnahme.
   */
  private baueNeu(): AudioContext | null {
    if (this.stopped || !this.ctx || !this.stream || !this.aufTon) return null;
    const AC =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    const spur = this.stream.getAudioTracks()[0];
    if (!AC || !spur || typeof spur.clone !== "function") return null;
    let neu: AudioContext;
    try {
      neu = new AC();
    } catch {
      return null;
    }
    try {
      this.processor?.disconnect();
      this.source?.disconnect();
    } catch {
      /* schon getrennt */
    }
    this.abgeloesteKontexte.push(this.ctx);
    this.ctx = neu;
    void neu.resume().catch(() => undefined);
    const kopie = spur.clone();
    kopie.enabled = !this.stumm;
    this.spurKopien.push(kopie);
    this.takt = new Heruntertakter(Math.round(neu.sampleRate), this.messung.rate || undefined);
    this.source = neu.createMediaStreamSource(new MediaStream([kopie]));
    this.processor = neu.createScriptProcessor(this.blockGroesse(), 1, 1);
    this.processor.onaudioprocess = this.aufTon;
    this.source.connect(this.processor);
    const mute = neu.createGain();
    mute.gain.value = 0;
    this.processor.connect(mute);
    mute.connect(neu.destination);
    return neu;
  }

  /**
   * Ticket 2519 (herausgelöst 01.10.2026, Ticket 2867): wie weit ein Wort, das
   * auf der Tonachse bei `endeMs` endet, hinter der Uhr liegt — jetzt minus
   * Tonanfang minus Wortende. `null`, solange es keine Tonachse oder kein
   * bestätigtes Wort gibt.
   */
  private nachlaufMs(endeMs: number): number | null {
    if (endeMs < 0) return null;
    const uhr = this.uhrAusTon(endeMs);
    return uhr === null ? null : performance.now() - uhr;
  }

  /**
   * Bestätigte UND vorläufige Wörter dieser Nachricht, mit Uhrzeiten der Seite.
   * (03.10.2026, Fantasiewörter — frontend/lib/rausch-woerter.ts) Ein
   * vorläufiges Teilstück ohne Leerraum hängt am letzten bestätigten Wort.
   */
  private alleWoerter(vorlaeufig: readonly SonioxToken[]): LiveWort[] {
    const alle = this.finaleWoerter.map((w) => ({ ...w }));
    for (const t of vorlaeufig) haengeWortAn(alle, t);
    return alle.map((w) => ({
      ...w,
      uhrStartMs: w.startMs === null ? null : this.uhrAusTon(w.startMs),
      uhrEndMs: w.endMs === null ? null : this.uhrAusTon(w.endMs),
    }));
  }

  /** Wann (performance.now) ein Punkt der Soniox-Tonachse gesprochen wurde — `null` vor dem ersten Rahmen. */
  private uhrAusTon(tonMs: number): number | null {
    const m = this.messung;
    if (m.tonNullMs === null) return null;
    /*
     * 23.09.2026 (Nachprüfung): Fielen Vorlauf-Rahmen weg, fehlt ihre
     * Dauer auf der Soniox-Tonachse. Ein Wort HINTER der Lücke wurde
     * um genau diese Dauer später gesprochen, als end_ms sagt — ohne
     * Ausgleich stünde der Nachlauf um die Lücke zu hoch da. Wörter aus
     * dem aufgehobenen Vorlauf liegen davor und bleiben unverändert.
     *
     * ⚠️ Nicht ausgeglichen: ein Kontext, der mitten in der Aufnahme
     *    anhält und von selbst wieder anläuft (iOS nach einer
     *    Unterbrechung). LiveDictation weckt ihn nie selbst; bleibt er
     *    stehen, kommen danach nur noch Wörter von davor, und deren
     *    Nachlauf stimmt.
     */
    const luecke = m.lueckeAbMs !== null && tonMs > m.lueckeAbMs ? m.vorlaufVerlorenMs : 0;
    return m.tonNullMs + tonMs + luecke;
  }

  /** Ticket 2519: Gesendetes zählen und den Sendepuffer im Blick behalten. */
  private merkeGesendet(ws: WebSocket, bytes: number): void {
    this.messung.gesendetBytes += bytes;
    this.messung.pufferMax = Math.max(this.messung.pufferMax, ws.bufferedAmount);
  }

  /**
   * Ticket 2519: die Zeile `[diktat]` — genau einmal, über denselben Weg wie
   * `[ptt-t]`. `nachlauf` ist der Median, `nachlaufMax` der schlimmste Fall.
   */
  private meldeMessung(): void {
    const m = this.messung;
    if (m.gemeldet) return;
    m.gemeldet = true;
    const s = [...m.nachlaeufe].sort((a, b) => a - b);
    const ms = (v: number | undefined): string =>
      v === undefined ? "-" : `${Math.round(v)}ms`;
    meldeSpur(
      "diktat",
      `rate=${m.rate} gesendetKB=${Math.round(m.gesendetBytes / 1024)}` +
        ` pufferMax=${m.pufferMax}B` +
        ` erstesWort=${m.erstesWortMs < 0 ? "-" : `${m.erstesWortMs}ms`}` +
        ` nachlauf=${ms(s[Math.floor((s.length - 1) / 2)])}` +
        ` nachlaufMax=${ms(s[s.length - 1])} proben=${s.length}` +
        ` vorlaufVerloren=${Math.round(m.vorlaufVerlorenMs)}ms` +
        ` geliehen=${this.geliehen ? "ja" : "nein"}`,
    );
  }

  /** Diktat beenden: Rest-Audio finalisieren, alles sauber schließen. */
  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    this.leitungAbmelden?.();
    this.leitungAbmelden = null;
    this.taktWache?.stop();
    this.taktWache = null;
    try {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send("");
    } catch {
      /* egal */
    }
    try {
      this.processor?.disconnect();
      this.source?.disconnect();
    } catch {
      /* egal */
    }
    // Kam die Leitung nie zustande, liegt hier noch Ton — weg damit.
    this.vorlauf = [];
    // Geliehener Strom gehoert dem Aufnehmer — nicht abwuergen.
    if (!this.geliehen) this.stream?.getTracks().forEach((t) => t.stop());
    // Stabilisierung Teil 2: pausierte Tagesaufnahme wieder anwerfen.
    void import("./native-recorder.js")
      .then((m) => m.resumeDayRecorderAfterMic("diktat"))
      .catch(() => undefined);
    for (const t of this.spurKopien.splice(0)) {
      try {
        t.stop();
      } catch {
        /* schon gestoppt */
      }
    }
    for (const k of [this.ctx, ...this.abgeloesteKontexte.splice(0)]) {
      try {
        await k?.close();
      } catch {
        /* egal */
      }
    }
    // WS erst nach kurzer Frist schließen, damit letzte finale Tokens ankommen.
    const ws = this.ws;
    window.setTimeout(() => {
      try {
        ws?.close();
      } catch {
        /* egal */
      }
    }, 1500);
    this.processor = null;
    this.source = null;
    this.stream = null;
    this.ctx = null;
  }

  /**
   * 2026-07-25 (Martin: "wenn ich frueh auf Stop druecke, fehlt ein Teil —
   * kann das nachgereicht werden?"): Beenden UND auf den Abschluss warten.
   *
   * stop() schickt Soniox das Ende-Signal und laesst die Leitung noch kurz
   * offen; die letzten bestaetigten Woerter kommen also durchaus an. Sie
   * wurden bisher nur nicht mehr uebernommen. Diese Methode wartet darauf und
   * liefert den VOLLSTAENDIGEN Text.
   *
   * Gedeckelt, damit es nie haengt: kommt kein Abschluss, gilt nach Ablauf
   * das, was bis dahin bestaetigt ist. Ein fehlendes Wort ist aergerlich, ein
   * haengender Knopf waere schlimmer.
   */
  async stopUndAbschluss(maxWartenMs = 1400): Promise<string> {
    if (this.stopped) return mitJevSchreibweise(this.finalText.trim());
    const abschluss = new Promise<void>((resolve) => {
      this.abschlussFertig = resolve;
    });
    const t0 = performance.now();
    void this.stop();
    await Promise.race([
      abschluss,
      new Promise<void>((r) => window.setTimeout(r, maxWartenMs)),
    ]);
    this.abschlussFertig = null;
    this.abschlussMs = Math.round(performance.now() - t0);
    return mitJevSchreibweise(this.finalText.trim());
  }

  /** Mittlere Zuversicht über alle bestätigten Wörter, die eine haben (0…1); `null`, wenn keines eine hat. */
  private konfidenzMittel(): number | null {
    const k = this.finaleWoerter.map((w) => w.konfidenz).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (k.length === 0) return null;
    return Math.round((k.reduce((a, b) => a + b, 0) / k.length) * 1000) / 1000;
  }

  /**
   * Was der Server über diese Mitschrift wissen muss (12.10.2026, Bahn live-primaer): kam der Abschluss,
   * riss die Leitung, wie viel Ton kam bei Soniox an, wie viel ging verloren. Nur Zahlen und Ja/Nein.
   * Gelesen NACH `stopUndAbschluss`.
   */
  stand(): LiveStand {
    const m = this.messung;
    return {
      abschluss: this.abschlussGesehen,
      abbruch: this.abgebrochen,
      // 16 kHz, 16 Bit, mono: zwei Bytes je Wert, `rate` Werte je Sekunde.
      tonMs: m.rate > 0 ? Math.round((m.gesendetBytes / (m.rate * 2)) * 1000) : 0,
      verlorenMs: Math.round(m.vorlaufVerlorenMs),
      abschlussMs: this.abschlussMs,
      endeLetztesWortMs: this.letztesWortEndeMs >= 0 ? Math.round(this.letztesWortEndeMs) : null,
      konfidenzMittel: this.konfidenzMittel(),
    };
  }

  /** Bisher bestätigter Text (ohne interim). */
  getFinalText(): string {
    return mitJevSchreibweise(this.finalText.trim());
  }
}
